import React, { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import axiosInstance from '../axiosInstance';
import { useSnackbar } from '../../providers/SnackbarProvider';

/**
 * Kompakte Datei-Liste für die Stammdaten-Randspalte (Ruhe-Prinzip:
 * schlichte Zeilen + „+ Hochladen"-Textlink statt DataGrid-Tabelle).
 * Klick auf den Namen lädt die Datei herunter; Löschen erscheint beim Zeigen.
 */
const FileRail = ({ relatedType, relatedId, onCountChange, disabled }) => {
  const { showSnackbar } = useSnackbar();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const publish = (list) => {
    setFiles(list);
    if (onCountChange) onCountChange(list.length);
  };

  useEffect(() => {
    const fetchFiles = async () => {
      try {
        const response = await axiosInstance.get('/attachments/', {
          params: { object_type: relatedType, object_id: relatedId },
        });
        setFiles(response.data);
        if (onCountChange) onCountChange(response.data.length);
      } catch (error) {
        console.error('Fehler beim Laden der Dateien:', error);
      }
    };
    fetchFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relatedType, relatedId]);

  const uploadFile = async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('description', '');
    formData.append('object_type', relatedType);
    formData.append('object_id', relatedId);
    setUploading(true);
    try {
      const response = await axiosInstance.post('/attachments/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      publish([response.data, ...files]);
      showSnackbar('Datei hochgeladen', 'success');
    } catch (error) {
      console.error('Upload fehlgeschlagen:', error);
      showSnackbar(error.response?.data?.message || 'Upload fehlgeschlagen', 'error');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const download = async (file) => {
    try {
      const response = await axiosInstance.get(`/attachments/${file.id}/download/`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.name || `attachment_${file.id}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download fehlgeschlagen:', error);
      showSnackbar('Download fehlgeschlagen', 'error');
    }
  };

  const remove = async (fileId) => {
    if (!window.confirm('Diese Datei wirklich löschen?')) return;
    try {
      await axiosInstance.delete(`/attachments/${fileId}/`);
      publish(files.filter((f) => f.id !== fileId));
      showSnackbar('Datei gelöscht', 'success');
    } catch (error) {
      console.error('Löschen fehlgeschlagen:', error);
      showSnackbar('Löschen fehlgeschlagen', 'error');
    }
  };

  return (
    <Box>
      {files.map((file) => (
        <Box key={file.id} sx={{
          display: 'flex', alignItems: 'center', gap: 1, py: 0.5,
          '&:hover .file-actions': { opacity: 1 },
        }}>
          <InsertDriveFileOutlinedIcon sx={{ fontSize: 17, color: 'text.secondary', flex: 'none' }} />
          <Typography
            variant="body2" noWrap
            onClick={() => download(file)}
            sx={{ cursor: 'pointer', minWidth: 0, '&:hover': { color: 'primary.main', textDecoration: 'underline' } }}
            title={file.name}
          >
            {file.name}
          </Typography>
          <Box className="file-actions" sx={{ opacity: 0, transition: 'opacity .15s', ml: 'auto', flex: 'none' }}>
            {!disabled && (
              <Tooltip title="Löschen">
                <IconButton size="small" onClick={() => remove(file.id)}><DeleteIcon sx={{ fontSize: 15 }} /></IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      ))}
      {files.length === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', py: 0.5 }}>
          Noch keine Dateien.
        </Typography>
      )}
      {!disabled && (
        <>
          <input
            ref={inputRef} type="file" hidden
            onChange={(e) => uploadFile(e.target.files && e.target.files[0])}
          />
          <Button size="small" sx={{ mt: 0.5, px: 0.5 }} disabled={uploading} onClick={() => inputRef.current && inputRef.current.click()}>
            {uploading ? 'Lädt hoch …' : '+ Hochladen'}
          </Button>
        </>
      )}
    </Box>
  );
};

export default FileRail;
